import { useState, useEffect } from "react";

// ─── Global CSS injected once ────────────────────────────────────────────────
const GLOBAL_CSS = `
  @keyframes fadeIn  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-20px); } to { opacity:1; transform:translateX(0); } }
  @keyframes spin    { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }

  .fade-in  { animation: fadeIn  0.4s ease forwards; }
  .slide-in { animation: slideIn 0.4s ease forwards; }

  input, textarea, select { font-family: 'DM Sans', sans-serif; outline: none; transition: border-color .2s, box-shadow .2s; }
  input:focus, textarea:focus { border-color: #C8A951 !important; box-shadow: 0 0 0 3px rgba(200,169,81,.15) !important; }

  button { cursor:pointer; font-family:'DM Sans',sans-serif; transition:all .2s; }
  button:active { transform:scale(.97); }

  ::-webkit-scrollbar       { width:6px; }
  ::-webkit-scrollbar-track { background:#D7CCC8; }
  ::-webkit-scrollbar-thumb { background:#4E342E; border-radius:3px; }

  /* Dark-mode overrides */
  .dm-root { background:#1a1008 !important; color:#F5EFE8 !important; }
  .dm-card { background:#2a1c14 !important; border-color:#3d2a1f !important; color:#F5EFE8 !important; }
  .dm-sidebar { background:#120b05 !important; }
  .dm-nav  { background:#1a1008 !important; border-color:#3d2a1f !important; }
  .dm-input { background:#2a1c14 !important; color:#F5EFE8 !important; border-color:#3d2a1f !important; }
  .dm-table-row:hover { background:#2a1c14 !important; }
  .dm-inner { background:#1a1008 !important; }
  .dm-sub   { background:#1e1208 !important; }

  @media print { .no-print { display:none !important; } }

  /* Responsive */
  @media (max-width:900px) {
    .main-grid  { grid-template-columns: 1fr !important; }
    .chart-grid { grid-template-columns: 1fr !important; }
    .agent-grid { grid-template-columns: 1fr !important; }
    .score-grid { grid-template-columns: 1fr !important; }
  }
  @media (max-width:600px) {
    .stats-grid { grid-template-columns: 1fr 1fr !important; }
    .login-left { display:none !important; }
    .login-right { width:100% !important; padding:32px 24px !important; }
  }
`;

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  primary : "#4E342E",
  accent  : "#C8A951",
  cream   : "#FAF6F1",
  secondary:"#D7CCC8",
  text    : "#2C1A14",
  muted   : "#8D7B75",
  border  : "#E8DDD8",
  danger  : "#C62828",
  success : "#2E7D32",
  warning : "#E65100",
};

// ─── Mock DB & AI Engine ──────────────────────────────────────────────────────
const USERS_DB = {
  "admin@casusai.com": { password: "Demo@1234", name: "Arjun Mehta", role: "Senior Partner" },
};

const MOCK_CASES = [
  { id:1, title:"Contract Breach – Apex Builders",   date:"2026-02-18", score:82, risk:"Low",      rec:"Proceed", financial:850000  },
  { id:2, title:"Employment Dispute – RK Corp",       date:"2026-02-10", score:47, risk:"High",     rec:"Revise",  financial:320000  },
  { id:3, title:"IP Infringement – SoftX Ltd",        date:"2026-01-28", score:71, risk:"Moderate", rec:"Proceed", financial:1200000 },
  { id:4, title:"Property Dispute – Sharma Estate",   date:"2026-01-15", score:33, risk:"High",     rec:"Stop",    financial:500000  },
];

function generateAnalysis(desc, evidence, financial, withContract = true) {
  const hasContract = withContract && evidence.includes("Written contract");
  const hasPayment  = evidence.includes("Payment proof");
  const hasWitness  = evidence.includes("Witness");
  const hasNone     = evidence.includes("None");

  let s = 40;
  if (hasContract) s += 25;
  if (hasPayment)  s += 15;
  if (hasWitness)  s += 10;
  if (hasNone)     s -= 10;
  if (financial > 500000) s += 5;
  s = Math.max(10, Math.min(95, s + Math.floor(Math.random() * 10 - 5)));

  const risk     = s >= 70 ? "Low" : s >= 45 ? "Moderate" : "High";
  const confidence = s >= 70 ? "High" : s >= 45 ? "Medium" : "Low";
  const rec      = s >= 68 ? "Proceed" : s >= 45 ? "Revise" : "Stop";
  const evidStr  = hasContract && hasPayment ? "Strong" : (hasContract || hasPayment) ? "Moderate" : "Weak";

  return {
    score: s, risk, confidence, rec, evidenceStrength: evidStr, financial,
    clerk: {
      summary: `Dispute involves ${desc.length > 80 ? desc.slice(0,80)+"…" : desc}. ${hasContract ? "A written agreement forms the legal backbone." : "No formal contract was established."} ${hasPayment ? "Financial transactions are documented." : ""}`,
      factors: [
        hasContract ? "Contractual obligation clearly defined" : "Absence of written agreement weakens standing",
        hasPayment  ? "Documentary evidence of financial exchange exists" : "Lack of payment proof is a liability",
        hasWitness  ? "Witness testimony can corroborate key facts" : "No witness support identified",
      ],
    },
    advocate: {
      risks: [
        s < 60 ? "Opposing counsel may challenge absence of formal documentation" : "Opponent may argue contract ambiguity or force majeure",
        financial > 500000 ? "High financial stakes increase opponent's incentive to litigate aggressively" : "Limited financial exposure may reduce opposition's leverage",
        hasNone ? "Without evidence, burden of proof rests entirely on oral testimony" : "Evidence may be challenged on admissibility grounds",
      ],
      counter: hasWitness ? "Opposing party may produce counter-witnesses or discredit existing testimony" : "Without witnesses, opponent can deny all verbal commitments",
    },
    strategist: {
      evaluation: `${rec === "Proceed" ? "Case presents viable grounds for litigation with available evidence." : rec === "Revise" ? "Case requires strengthening before proceeding." : "Current evidence posture does not support litigation."} ${hasContract && hasPayment ? "Strong documentary foundation." : "Evidence gaps pose meaningful risk."}`,
    },
  };
}

// ─── Tiny SVG Icons ───────────────────────────────────────────────────────────
const ScalesIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
    <rect x="30" y="4"  width="4"  height="56" rx="2"   fill={C.accent}/>
    <rect x="12" y="6"  width="40" height="3"  rx="1.5" fill={C.accent}/>
    <line x1="32" y1="7" x2="16" y2="22" stroke={C.accent} strokeWidth="1.5"/>
    <line x1="32" y1="7" x2="48" y2="22" stroke={C.accent} strokeWidth="1.5"/>
    <ellipse cx="16" cy="29" rx="10" ry="4" fill={C.primary} stroke={C.accent} strokeWidth="1.5"/>
    <ellipse cx="48" cy="26" rx="10" ry="4" fill={C.primary} stroke={C.accent} strokeWidth="1.5"/>
    <rect x="22" y="58" width="20" height="3" rx="1.5" fill={C.accent}/>
  </svg>
);

const GavelIcon = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
    <rect x="8"  y="48" width="32" height="8"  rx="3" fill={C.accent}/>
    <rect x="36" y="12" width="12" height="32" rx="3" transform="rotate(-45 36 12)" fill={C.primary} stroke={C.accent} strokeWidth="1.5"/>
    <rect x="20" y="8"  width="8"  height="20" rx="2" transform="rotate(-45 20 8)"  fill={C.accent}/>
  </svg>
);

const RiskGauge = ({ score }) => {
  const angle = -135 + (score / 100) * 270;
  const color = score >= 70 ? "#22c55e" : score >= 45 ? "#f59e0b" : "#ef4444";
  return (
    <svg viewBox="0 0 120 80" style={{ width:"100%", maxWidth:180 }}>
      <path d="M10 70 A50 50 0 1 1 110 70" stroke="#D7CCC8" strokeWidth="10" fill="none" strokeLinecap="round"/>
      <path d="M10 70 A50 50 0 1 1 110 70" stroke={color} strokeWidth="10" fill="none" strokeLinecap="round" strokeDasharray={`${(score/100)*157} 157`}/>
      <g transform={`translate(60,70) rotate(${angle})`}>
        <line x1="0" y1="0" x2="0" y2="-35" stroke={C.primary} strokeWidth="3" strokeLinecap="round"/>
        <circle r="5" fill={C.primary}/>
      </g>
      <text x="60" y="76" textAnchor="middle" fontSize="14" fontWeight="bold" fill={C.primary}>{score}</text>
    </svg>
  );
};

// ─── Donut Chart ──────────────────────────────────────────────────────────────
function DonutChart({ cases, dm }) {
  const low  = cases.filter(c => c.risk === "Low").length;
  const mod  = cases.filter(c => c.risk === "Moderate").length;
  const high = cases.filter(c => c.risk === "High").length;
  const total = cases.length || 1;
  const segs = [
    { val:low,  color:"#22c55e", label:"Low Risk"  },
    { val:mod,  color:"#f59e0b", label:"Moderate"  },
    { val:high, color:"#ef4444", label:"High Risk"  },
  ];
  const cx=60, cy=60, r=45, inn=28;
  let cum=0;
  const paths = segs.map(s => {
    const start=cum, angle=(s.val/total)*360; cum+=angle;
    const sr=(start-90)*Math.PI/180, er=(start+angle-90)*Math.PI/180;
    const x1=cx+r*Math.cos(sr),  y1=cy+r*Math.sin(sr);
    const x2=cx+r*Math.cos(er),  y2=cy+r*Math.sin(er);
    const ix1=cx+inn*Math.cos(sr), iy1=cy+inn*Math.sin(sr);
    const ix2=cx+inn*Math.cos(er), iy2=cy+inn*Math.sin(er);
    const lg=angle>180?1:0;
    return { ...s, d:`M ${x1} ${y1} A ${r} ${r} 0 ${lg} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${inn} ${inn} 0 ${lg} 0 ${ix1} ${iy1} Z` };
  });
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
      <svg viewBox="0 0 120 120" style={{ width:130, height:130 }}>
        {paths.map((p,i) => <path key={i} d={p.d} fill={p.color} opacity={.85}/>)}
        <text x={cx} y={cy-4} textAnchor="middle" fontSize="14" fontWeight="bold" fill={dm?"#F5EFE8":C.primary}>{total}</text>
        <text x={cx} y={cy+10} textAnchor="middle" fontSize="8" fill={C.muted}>Cases</text>
      </svg>
      <div style={{ width:"100%", marginTop:8 }}>
        {segs.map(s => (
          <div key={s.label} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:8, height:8, borderRadius:2, background:s.color }}/>
              <span style={{ fontSize:12, color:C.muted }}>{s.label}</span>
            </div>
            <span style={{ fontSize:12, fontWeight:600, color:dm?"#F5EFE8":C.text }}>{s.val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Agent Progress ───────────────────────────────────────────────────────────
function AgentProgress() {
  const [step, setStep] = useState(0);
  const steps = ["Legal Clerk reviewing case facts…", "Devil's Advocate analyzing risks…", "Lead Strategist computing viability…"];
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s < 2 ? s+1 : 2)), 900);
    return () => clearInterval(t);
  }, []);
  return (
    <div>
      {steps.map((s,i) => (
        <div key={s} style={{ display:"flex", alignItems:"center", gap:10, padding:"6px 0" }}>
          <div style={{ width:16, height:16, borderRadius:"50%", background:i<=step?C.accent:"rgba(200,169,81,.2)", border:`2px solid ${C.accent}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, color:i<=step?"#4E342E":"transparent", transition:"all .3s" }}>✓</div>
          <span style={{ fontSize:12, color:i<=step?C.accent:C.muted, fontWeight:i<=step?600:400 }}>{s}</span>
        </div>
      ))}
    </div>
  );
}

// ─── AgentCard ────────────────────────────────────────────────────────────────
function AgentCard({ title, subtitle, icon, dm, children }) {
  return (
    <div className={dm?"dm-card":""} style={{ background:dm?"#2a1c14":"#fff", borderRadius:12, padding:"20px 22px", border:`1px solid ${dm?"#3d2a1f":C.border}`, boxShadow:"0 2px 12px rgba(78,52,46,.05)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14, paddingBottom:12, borderBottom:`1px solid ${dm?"#3d2a1f":C.border}` }}>
        <span style={{ fontSize:18 }}>{icon}</span>
        <div>
          <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:15, color:dm?"#F5EFE8":C.primary }}>{title}</div>
          <div style={{ fontSize:11, color:C.muted }}>{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

// ─── Shared Card wrapper ──────────────────────────────────────────────────────
function Card({ dm, style={}, children, className="" }) {
  return (
    <div
      className={`${dm?"dm-card":""} ${className}`}
      style={{ background:dm?"#2a1c14":"#fff", borderRadius:12, border:`1px solid ${dm?"#3d2a1f":C.border}`, boxShadow:"0 2px 12px rgba(78,52,46,.06)", ...style }}
    >{children}</div>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function LoginPage({ onLogin, onGoRegister, dm }) {
  const [email, setEmail]       = useState("admin@casusai.com");
  const [password, setPassword] = useState("Demo@1234");

  return (
    <div style={{ display:"flex", minHeight:"100vh" }}>
      {/* Left */}
      <div className="login-left" style={{ flex:1, background:C.primary, display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", padding:48, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, background:"radial-gradient(ellipse at 30% 50%, rgba(200,169,81,.12), transparent 60%)" }}/>
        <div style={{ position:"absolute", top:40,    right:40, opacity:.07 }}><ScalesIcon size={200}/></div>
        <div style={{ position:"absolute", bottom:60, left:40,  opacity:.06 }}><GavelIcon  size={140}/></div>
        <div style={{ position:"relative", textAlign:"center", maxWidth:380 }}>
          <ScalesIcon size={64}/>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, margin:"20px 0 12px" }}>
            <h1 style={{ fontFamily:"'Playfair Display',serif", color:"#fff", fontSize:42, fontWeight:700, lineHeight:1 }}>Casus</h1>
            <span style={{ background:C.accent, color:"#4E342E", fontWeight:800, fontSize:18, padding:"4px 10px", borderRadius:6, letterSpacing:".06em" }}>AI</span>
          </div>
          <p style={{ color:"rgba(215,204,200,.75)", fontSize:15, lineHeight:1.7 }}>Strategic Legal Decision Support System.</p>
          <p style={{ color:C.accent, fontSize:13, fontStyle:"italic", marginTop:6, marginBottom:36, opacity:.85 }}>"Every case. Clearly argued."</p>
          <div style={{ display:"flex", justifyContent:"center", gap:32 }}>
            {[["2,400+","Cases Analyzed"],["94%","Accuracy Rate"],["48hrs","Time Saved"]].map(([n,l]) => (
              <div key={l} style={{ textAlign:"center" }}>
                <div style={{ color:C.accent, fontWeight:700, fontSize:20, fontFamily:"'Playfair Display',serif" }}>{n}</div>
                <div style={{ color:"rgba(215,204,200,.5)", fontSize:11, marginTop:2 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="login-right" style={{ width:480, background:dm?"#1a1008":"#fff", display:"flex", alignItems:"center", justifyContent:"center", padding:"48px 56px" }}>
        <div style={{ width:"100%", animation:"fadeIn .5s ease" }}>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:C.primary, marginBottom:4 }}>Welcome back</h2>
          <p style={{ color:C.muted, fontSize:13, marginBottom:6 }}>Sign in to your CasusAI workspace</p>
          <div style={{ display:"inline-block", fontSize:11, color:C.accent, background:"rgba(200,169,81,.1)", border:"1px solid rgba(200,169,81,.25)", borderRadius:20, padding:"3px 10px", marginBottom:28, fontStyle:"italic" }}>
            casus — Latin for legal case
          </div>

          {[
            { label:"Email Address", val:email,    set:setEmail,    type:"text"     },
            { label:"Password",      val:password, set:setPassword, type:"password" },
          ].map(f => (
            <div key={f.label} style={{ marginBottom:18 }}>
              <label style={{ display:"block", fontSize:11, fontWeight:600, color:C.primary, marginBottom:6, letterSpacing:".05em", textTransform:"uppercase" }}>{f.label}</label>
              <input type={f.type} value={f.val} onChange={e => f.set(e.target.value)}
                className={dm?"dm-input":""}
                style={{ width:"100%", padding:"12px 16px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, background:dm?"#2a1c14":"#FAFAFA", color:dm?"#F5EFE8":C.text }}/>
            </div>
          ))}

          <button onClick={() => onLogin(email, password)}
            style={{ width:"100%", padding:13, background:C.primary, color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:600, letterSpacing:".02em", marginBottom:10, marginTop:8 }}>
            Sign In to CasusAI
          </button>
          <button onClick={() => onLogin(email, password)}
            style={{ width:"100%", padding:12, background:"none", color:C.accent, border:`1px solid ${C.accent}`, borderRadius:8, fontSize:14, fontWeight:500, marginBottom:24 }}>
            ⚡ Quick Demo Access
          </button>
          <p style={{ textAlign:"center", fontSize:13, color:C.muted }}>
            No account?{" "}
            <button onClick={onGoRegister} style={{ background:"none", border:"none", color:C.accent, fontWeight:600, fontSize:13 }}>Register</button>
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── REGISTER PAGE ────────────────────────────────────────────────────────────
function RegisterPage({ onRegister, onGoLogin, dm }) {
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");

  return (
    <div style={{ display:"flex", minHeight:"100vh", background:dm?"#1a1008":C.cream, alignItems:"center", justifyContent:"center" }}>
      <div className={dm?"dm-card":""} style={{ background:dm?"#2a1c14":"#fff", borderRadius:16, padding:"48px 52px", width:"100%", maxWidth:460, boxShadow:"0 20px 60px rgba(78,52,46,.12)", animation:"fadeIn .4s ease" }}>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <ScalesIcon size={48}/>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8, margin:"16px 0 6px" }}>
            <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:24, color:C.primary }}>Casus</span>
            <span style={{ background:C.accent, color:"#4E342E", fontWeight:800, fontSize:12, padding:"2px 8px", borderRadius:4 }}>AI</span>
          </div>
          <p style={{ color:C.muted, fontSize:13 }}>Create your CasusAI account</p>
        </div>

        {[
          { label:"Full Name",      val:name,     set:setName,     type:"text"     },
          { label:"Email Address",  val:email,    set:setEmail,    type:"email"    },
          { label:"Password",       val:password, set:setPassword, type:"password" },
        ].map(f => (
          <div key={f.label} style={{ marginBottom:18 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:600, color:C.primary, marginBottom:5, letterSpacing:".06em", textTransform:"uppercase" }}>{f.label}</label>
            <input type={f.type} value={f.val} onChange={e => f.set(e.target.value)}
              className={dm?"dm-input":""}
              style={{ width:"100%", padding:"11px 14px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, background:dm?"#1a1008":"#FAFAFA", color:dm?"#F5EFE8":C.text }}/>
          </div>
        ))}

        <button onClick={() => onRegister(name, email, password)}
          style={{ width:"100%", padding:13, background:C.primary, color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:600, marginTop:8, marginBottom:14 }}>
          Create CasusAI Account
        </button>
        <p style={{ textAlign:"center", fontSize:13, color:C.muted }}>
          Already registered?{" "}
          <button onClick={onGoLogin} style={{ background:"none", border:"none", color:C.accent, fontWeight:600, fontSize:13 }}>Sign In</button>
        </p>
      </div>
    </div>
  );
}

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
function Sidebar({ open, page, setPage, user, onLogout, dm }) {
  const navItems = [
    { id:"dashboard", icon:"⊞", label:"Dashboard"    },
    { id:"analyze",   icon:"⊕", label:"New Analysis" },
    { id:"history",   icon:"≡", label:"Case History" },
  ];
  return (
    <aside className={`${dm?"dm-sidebar":""} no-print`}
      style={{ width:open?240:0, minWidth:open?240:0, overflow:"hidden", background:dm?"#120b05":C.primary,
               display:"flex", flexDirection:"column", transition:"all .3s ease", position:"sticky", top:0, height:"100vh", flexShrink:0 }}>
      {/* Logo */}
      <div style={{ padding:"28px 20px 20px", borderBottom:"1px solid rgba(255,255,255,.1)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <ScalesIcon size={36}/>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <span style={{ fontFamily:"'Playfair Display',serif", color:"#fff", fontWeight:700, fontSize:18 }}>Casus</span>
              <span style={{ background:C.accent, color:"#4E342E", fontWeight:800, fontSize:10, padding:"1px 6px", borderRadius:3 }}>AI</span>
            </div>
            <div style={{ color:"rgba(215,204,200,.5)", fontSize:10, letterSpacing:".08em", textTransform:"uppercase", marginTop:2 }}>Legal Intelligence</div>
          </div>
        </div>
      </div>
      {/* User */}
      <div style={{ padding:"16px 20px", borderBottom:"1px solid rgba(255,255,255,.08)" }}>
        <div style={{ color:C.secondary, fontSize:13, fontWeight:500 }}>{user.name}</div>
        <div style={{ color:"rgba(215,204,200,.5)", fontSize:11 }}>{user.role}</div>
      </div>
      {/* Nav */}
      <nav style={{ flex:1, padding:"16px 12px" }}>
        {navItems.map(n => (
          <button key={n.id} onClick={() => setPage(n.id)}
            style={{ display:"flex", alignItems:"center", gap:12, width:"100%", padding:"11px 14px", borderRadius:8,
                     background:page===n.id?"rgba(200,169,81,.2)":"none",
                     border:page===n.id?"1px solid rgba(200,169,81,.3)":"1px solid transparent",
                     color:page===n.id?C.accent:"rgba(215,204,200,.7)", fontSize:14,
                     fontWeight:page===n.id?600:400, textAlign:"left", marginBottom:4, whiteSpace:"nowrap" }}>
            <span style={{ fontSize:16 }}>{n.icon}</span>{n.label}
          </button>
        ))}
      </nav>
      {/* Decorative gavel */}
      <div style={{ padding:"0 20px 8px", opacity:.1 }}><GavelIcon size={60}/></div>
      {/* Logout */}
      <div style={{ padding:"12px 12px 24px" }}>
        <button onClick={onLogout}
          style={{ display:"flex", alignItems:"center", gap:10, width:"100%", padding:"10px 14px", borderRadius:8,
                   background:"none", border:"1px solid rgba(255,255,255,.1)", color:"rgba(215,204,200,.6)", fontSize:13, textAlign:"left" }}>
          ⎋ Sign Out
        </button>
      </div>
    </aside>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ stats, cases, setPage, dm }) {
  const recColor = r => r==="Proceed"?C.success:r==="Revise"?C.warning:C.danger;
  const chartData = [...cases].reverse().slice(0, 6);
  return (
    <div className="fade-in">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:dm?"#F5EFE8":C.primary }}>Legal Intelligence Dashboard</h1>
        <p style={{ color:C.muted, fontSize:14, marginTop:4 }}>Overview of your CasusAI case analysis portfolio</p>
      </div>

      {/* Stat cards */}
      <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:28 }}>
        {[
          { label:"Total Cases Analyzed",   value:stats.total,            icon:"⚖", color:C.primary,  sub:"All time"          },
          { label:"Average Viability Score", value:`${stats.avgScore}/100`,icon:"◎", color:C.accent,   sub:"Portfolio average"  },
          { label:"High Risk Cases",         value:stats.highRisk,         icon:"⚠", color:C.danger,   sub:"Requires attention" },
          { label:"Strong Cases",            value:stats.strong,           icon:"✦", color:C.success,  sub:"Score ≥ 70"         },
        ].map(c => (
          <Card key={c.label} dm={dm} style={{ padding:"20px 22px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
              <span style={{ fontSize:22 }}>{c.icon}</span>
              <span style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:".05em" }}>{c.sub}</span>
            </div>
            <div style={{ fontSize:30, fontWeight:700, color:c.color, fontFamily:"'Playfair Display',serif", marginBottom:4 }}>{c.value}</div>
            <div style={{ fontSize:12, color:C.muted }}>{c.label}</div>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="chart-grid" style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:16, marginBottom:28 }}>
        <Card dm={dm} style={{ padding:24 }}>
          <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:600, color:dm?"#F5EFE8":C.primary, marginBottom:20 }}>Viability Score — Recent Cases</h3>
          <div style={{ display:"flex", alignItems:"flex-end", gap:10, height:140 }}>
            {chartData.map((c,i) => (
              <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ fontSize:11, fontWeight:600, color:c.score>=70?"#22c55e":c.score>=45?"#f59e0b":"#ef4444" }}>{c.score}</div>
                <div style={{ width:"100%", borderRadius:"4px 4px 0 0", background:c.score>=70?"#22c55e":c.score>=45?"#f59e0b":"#ef4444", height:`${(c.score/100)*110}px`, opacity:.85 }}/>
                <div style={{ fontSize:9, color:C.muted }}>#{c.id}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:16, marginTop:16 }}>
            {[["#22c55e","Strong (≥70)"],["#f59e0b","Moderate (45-69)"],["#ef4444","High Risk (<45)"]].map(([col,lbl]) => (
              <div key={lbl} style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:10, height:10, borderRadius:2, background:col }}/>
                <span style={{ fontSize:11, color:C.muted }}>{lbl}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card dm={dm} style={{ padding:24 }}>
          <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:600, color:dm?"#F5EFE8":C.primary, marginBottom:20 }}>Risk Distribution</h3>
          <DonutChart cases={cases} dm={dm}/>
        </Card>
      </div>

      {/* Cases Table */}
      <Card dm={dm} style={{ overflow:"hidden" }}>
        <div style={{ padding:"20px 24px 16px", borderBottom:`1px solid ${dm?"#3d2a1f":C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:600, color:dm?"#F5EFE8":C.primary }}>Recent Case History</h3>
          <button onClick={() => setPage("analyze")} style={{ background:C.primary, color:"#fff", border:"none", borderRadius:6, padding:"7px 14px", fontSize:12, fontWeight:600 }}>+ New Analysis</button>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr className={dm?"dm-sub":""} style={{ background:dm?"#1e1208":"#FAF6F1" }}>
                {["Case Title","Date","Financial Exposure","Score","Risk","Recommendation"].map(h => (
                  <th key={h} style={{ padding:"10px 20px", textAlign:"left", fontSize:11, fontWeight:600, color:C.muted, textTransform:"uppercase", letterSpacing:".06em", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr key={c.id} className={dm?"dm-table-row":""} style={{ borderBottom:`1px solid ${dm?"#3d2a1f":C.border}`, transition:"background .15s" }}
                  onMouseEnter={e => e.currentTarget.style.background=dm?"#2a1c14":"#FBF8F5"}
                  onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                  <td style={{ padding:"13px 20px", fontSize:13, fontWeight:500, color:dm?"#F5EFE8":C.text, maxWidth:220, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{c.title}</td>
                  <td style={{ padding:"13px 20px", fontSize:12, color:C.muted, whiteSpace:"nowrap" }}>{c.date}</td>
                  <td style={{ padding:"13px 20px", fontSize:13, color:dm?"#F5EFE8":C.text }}>₹{c.financial.toLocaleString("en-IN")}</td>
                  <td style={{ padding:"13px 20px" }}>
                    <span style={{ fontSize:13, fontWeight:700, color:c.score>=70?C.success:c.score>=45?C.warning:C.danger }}>{c.score}/100</span>
                  </td>
                  <td style={{ padding:"13px 20px" }}>
                    <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:600,
                      background:c.risk==="Low"?"rgba(46,125,50,.12)":c.risk==="Moderate"?"rgba(230,81,0,.1)":"rgba(198,40,40,.1)",
                      color:c.risk==="Low"?C.success:c.risk==="Moderate"?C.warning:C.danger }}>{c.risk}</span>
                  </td>
                  <td style={{ padding:"13px 20px" }}>
                    <span style={{ fontSize:12, fontWeight:600, color:recColor(c.rec) }}>{c.rec}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── ANALYZE PAGE ─────────────────────────────────────────────────────────────
function AnalyzePage({ form, setForm, onSubmit, analyzing, dm }) {
  const evidenceOptions = ["Written contract","Payment proof","Witness","None"];
  const toggle = opt => setForm(p => ({
    ...p,
    evidence: p.evidence.includes(opt) ? p.evidence.filter(x => x!==opt) : [...p.evidence, opt],
  }));

  return (
    <div className="fade-in">
      <div style={{ marginBottom:28 }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:dm?"#F5EFE8":C.primary }}>New Case Analysis</h1>
        <p style={{ color:C.muted, fontSize:14, marginTop:4 }}>Provide case details for CasusAI multi-agent evaluation</p>
      </div>

      <div className="main-grid" style={{ display:"grid", gridTemplateColumns:"1fr 340px", gap:20, alignItems:"start" }}>
        {/* Form */}
        <Card dm={dm} style={{ padding:"32px 36px" }}>
          {/* Description */}
          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:C.muted, letterSpacing:".08em", textTransform:"uppercase", marginBottom:8 }}>Case Description *</label>
            <textarea value={form.desc} onChange={e => setForm(p => ({ ...p, desc:e.target.value }))}
              placeholder="Describe the dispute in detail. Include parties involved, timeline, key events…"
              rows={6} className={dm?"dm-input":""}
              style={{ width:"100%", padding:"14px 16px", border:`1px solid ${dm?"#3d2a1f":C.border}`, borderRadius:10, fontSize:14, resize:"vertical",
                       background:dm?"#1a1008":"#FAFAFA", color:dm?"#F5EFE8":C.text, lineHeight:1.6 }}/>
          </div>

          {/* Evidence */}
          <div style={{ marginBottom:24 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:C.muted, letterSpacing:".08em", textTransform:"uppercase", marginBottom:12 }}>Evidence Available *</label>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {evidenceOptions.map(opt => {
                const checked = form.evidence.includes(opt);
                return (
                  <label key={opt} style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", borderRadius:8, cursor:"pointer",
                    border:`1px solid ${checked?C.accent:dm?"#3d2a1f":C.border}`, background:checked?"rgba(200,169,81,.08)":dm?"#1a1008":"#FAFAFA", transition:"all .15s" }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(opt)} style={{ accentColor:C.accent, width:15, height:15 }}/>
                    <span style={{ fontSize:13, color:checked?C.accent:dm?"#D7CCC8":C.text, fontWeight:checked?600:400 }}>{opt}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Financial */}
          <div style={{ marginBottom:32 }}>
            <label style={{ display:"block", fontSize:11, fontWeight:700, color:C.muted, letterSpacing:".08em", textTransform:"uppercase", marginBottom:8 }}>Estimated Financial Loss (₹) *</label>
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", color:C.muted, fontSize:16, fontWeight:600 }}>₹</span>
              <input type="number" value={form.financial} onChange={e => setForm(p => ({ ...p, financial:e.target.value }))}
                placeholder="e.g., 300000" className={dm?"dm-input":""}
                style={{ width:"100%", padding:"12px 16px 12px 32px", border:`1px solid ${dm?"#3d2a1f":C.border}`, borderRadius:10,
                         fontSize:15, fontWeight:600, background:dm?"#1a1008":"#FAFAFA", color:dm?"#F5EFE8":C.text }}/>
            </div>
          </div>

          <button onClick={onSubmit} disabled={analyzing}
            style={{ width:"100%", padding:15, background:analyzing?C.muted:C.primary, color:"#fff", border:"none", borderRadius:10,
                     fontSize:15, fontWeight:700, letterSpacing:".03em", display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
            {analyzing
              ? <><div style={{ width:18, height:18, border:"2px solid rgba(255,255,255,.3)", borderTopColor:"#fff", borderRadius:"50%", animation:"spin .8s linear infinite" }}/> CasusAI Agents Analyzing…</>
              : "⚖ Analyze Case with CasusAI"}
          </button>

          {analyzing && (
            <div style={{ marginTop:20, padding:16, background:"rgba(200,169,81,.08)", borderRadius:10, border:"1px solid rgba(200,169,81,.2)" }}>
              <AgentProgress/>
            </div>
          )}
        </Card>

        {/* Side panel */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ background:C.primary, borderRadius:14, padding:24 }}>
            <ScalesIcon size={48}/>
            <div style={{ display:"flex", alignItems:"center", gap:8, margin:"14px 0 10px" }}>
              <h3 style={{ fontFamily:"'Playfair Display',serif", color:"#fff", fontSize:16, fontWeight:700 }}>Casus</h3>
              <span style={{ background:C.accent, color:"#4E342E", fontWeight:800, fontSize:10, padding:"1px 6px", borderRadius:3 }}>AI</span>
              <span style={{ color:"rgba(215,204,200,.6)", fontSize:13 }}>Multi-Agent Review</span>
            </div>
            {[["📋","Legal Clerk","Case summary & legal factors"],["⚔","Devil's Advocate","Risks & counterarguments"],["⚖","Lead Strategist","Final evaluation & score"]].map(([icon,title,desc]) => (
              <div key={title} style={{ display:"flex", gap:10, padding:10, background:"rgba(255,255,255,.06)", borderRadius:8, marginBottom:8 }}>
                <span style={{ fontSize:18 }}>{icon}</span>
                <div>
                  <div style={{ color:C.accent, fontWeight:600, fontSize:13 }}>{title}</div>
                  <div style={{ color:"rgba(215,204,200,.6)", fontSize:11, marginTop:1 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <Card dm={dm} style={{ padding:20 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:".07em", marginBottom:12 }}>Scoring Criteria</div>
            {[["Written Contract","+25 pts"],["Payment Proof","+15 pts"],["Witness Testimony","+10 pts"],["Financial Exposure","+5 pts"],["No Evidence","−10 pts"]].map(([l,p]) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${dm?"#3d2a1f":C.border}`, fontSize:12 }}>
                <span style={{ color:dm?"#D7CCC8":C.text }}>{l}</span>
                <span style={{ fontWeight:700, color:p.startsWith("+")?C.success:C.danger }}>{p}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── RESULTS PAGE ─────────────────────────────────────────────────────────────
function ResultsPage({ analysis, whatIf, whatIfAnalysis, onWhatIf, onDownload, onNewCase, dm }) {
  const a = whatIf && whatIfAnalysis ? whatIfAnalysis : analysis;
  const recColor = r => r==="Proceed"?C.success:r==="Revise"?C.warning:C.danger;
  const recBg    = r => r==="Proceed"?"rgba(46,125,50,.1)":r==="Revise"?"rgba(230,81,0,.1)":"rgba(198,40,40,.1)";

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom:24, display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:dm?"#F5EFE8":C.primary }}>CasusAI Analysis Report</h1>
          <p style={{ color:C.muted, fontSize:14, marginTop:4 }}>{new Date().toLocaleDateString("en-IN",{dateStyle:"long"})}</p>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onNewCase} className="no-print"
            style={{ padding:"9px 18px", background:"none", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, fontWeight:500, color:dm?"#D7CCC8":C.text }}>
            + New Case
          </button>
          <button onClick={onDownload} className="no-print"
            style={{ padding:"9px 18px", background:C.primary, border:"none", borderRadius:8, fontSize:13, fontWeight:600, color:"#fff" }}>
            ⬇ Download PDF
          </button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{ background:C.primary, borderRadius:14, padding:"20px 28px", marginBottom:20, display:"flex", flexWrap:"wrap", gap:0 }}>
        {[
          { label:"Risk Level",         value:a.risk,   color:a.risk==="Low"?"#22c55e":a.risk==="Moderate"?"#f59e0b":"#ef4444" },
          { label:"Financial Exposure", value:`₹${parseInt(a.financial).toLocaleString("en-IN")}`, color:C.accent },
          { label:"Evidence Strength",  value:a.evidenceStrength, color:C.secondary },
          { label:"Confidence",         value:a.confidence,       color:C.secondary },
        ].map((s,i) => (
          <div key={s.label} style={{ flex:"1 1 140px", padding:"0 20px", borderRight:i<3?"1px solid rgba(255,255,255,.1)":"none" }}>
            <div style={{ fontSize:10, color:"rgba(215,204,200,.5)", letterSpacing:".07em", textTransform:"uppercase", marginBottom:4 }}>{s.label}</div>
            <div style={{ fontSize:18, fontWeight:700, color:s.color, fontFamily:"'Playfair Display',serif" }}>{s.value}</div>
          </div>
        ))}
        <div style={{ flex:"1 1 140px", padding:"0 20px", display:"flex", flexDirection:"column", alignItems:"center" }}>
          <div style={{ fontSize:10, color:"rgba(215,204,200,.5)", letterSpacing:".07em", textTransform:"uppercase", marginBottom:4 }}>Recommendation</div>
          <span style={{ display:"inline-block", padding:"4px 14px", borderRadius:20, background:recBg(a.rec), border:`1px solid ${recColor(a.rec)}`, color:recColor(a.rec), fontWeight:700, fontSize:14 }}>{a.rec}</span>
        </div>
      </div>

      {/* Score + Legal Clerk */}
      <div className="score-grid" style={{ display:"grid", gridTemplateColumns:"220px 1fr", gap:16, marginBottom:16 }}>
        <Card dm={dm} style={{ padding:24, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.muted, letterSpacing:".07em", textTransform:"uppercase" }}>Viability Score</div>
          <RiskGauge score={a.score}/>
          <div style={{ fontSize:13, color:a.score>=70?C.success:a.score>=45?C.warning:C.danger, fontWeight:600 }}>
            {a.score>=70?"Strong Viability":a.score>=45?"Moderate Viability":"Low Viability"}
          </div>
          {whatIf && (
            <div style={{ fontSize:11, color:C.muted, textAlign:"center", padding:"6px 10px", background:"rgba(200,169,81,.1)", borderRadius:6 }}>What-If: Contract Removed</div>
          )}
        </Card>

        <AgentCard title="Legal Clerk" subtitle="Case Summary & Analysis" icon="📋" dm={dm}>
          <p style={{ fontSize:13, lineHeight:1.6, color:dm?"#D7CCC8":C.text, marginBottom:10 }}>{a.clerk.summary}</p>
          {a.clerk.factors.map((f,i) => (
            <div key={i} style={{ display:"flex", gap:8, marginBottom:5 }}>
              <span style={{ color:C.accent, fontSize:12, marginTop:1 }}>▸</span>
              <span style={{ fontSize:12, color:dm?"#D7CCC8":C.muted }}>{f}</span>
            </div>
          ))}
        </AgentCard>
      </div>

      {/* Devil + Strategist */}
      <div className="agent-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
        <AgentCard title="Devil's Advocate" subtitle="Risk & Counterarguments" icon="⚔" dm={dm}>
          <div style={{ fontSize:10, fontWeight:700, color:C.muted, letterSpacing:".07em", textTransform:"uppercase", marginBottom:8 }}>Key Risks</div>
          {a.advocate.risks.map((r,i) => (
            <div key={i} style={{ display:"flex", gap:8, marginBottom:6 }}>
              <span style={{ color:"#ef4444", fontSize:12, marginTop:1 }}>◆</span>
              <span style={{ fontSize:12, color:dm?"#D7CCC8":C.text, lineHeight:1.5 }}>{r}</span>
            </div>
          ))}
          <div style={{ padding:10, background:"rgba(198,40,40,.06)", borderRadius:8, borderLeft:"3px solid #ef4444", marginTop:8 }}>
            <div style={{ fontSize:10, fontWeight:700, color:C.muted, textTransform:"uppercase", letterSpacing:".06em", marginBottom:4 }}>Counterargument</div>
            <p style={{ fontSize:12, color:dm?"#D7CCC8":C.text, lineHeight:1.5 }}>{a.advocate.counter}</p>
          </div>
        </AgentCard>

        <AgentCard title="Lead Strategist" subtitle="Final Evaluation" icon="⚖" dm={dm}>
          <p style={{ fontSize:13, lineHeight:1.6, color:dm?"#D7CCC8":C.text, marginBottom:14 }}>{a.strategist.evaluation}</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
            {[
              { label:"Viability Score",  val:`${a.score}/100`, color:a.score>=70?C.success:a.score>=45?C.warning:C.danger },
              { label:"Confidence Level", val:a.confidence,     color:C.accent   },
              { label:"Evidence Strength",val:a.evidenceStrength,color:C.primary  },
              { label:"Risk Level",       val:a.risk,           color:a.risk==="Low"?C.success:a.risk==="Moderate"?C.warning:C.danger },
            ].map(item => (
              <div key={item.label} className={dm?"dm-inner":""} style={{ padding:"10px 12px", background:dm?"#1a1008":"#FAF6F1", borderRadius:8 }}>
                <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:".06em", marginBottom:3 }}>{item.label}</div>
                <div style={{ fontWeight:700, color:item.color, fontSize:15 }}>{item.val}</div>
              </div>
            ))}
          </div>
          <div style={{ padding:"12px 16px", borderRadius:10, background:recBg(a.rec), border:`1px solid ${recColor(a.rec)}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ fontSize:13, color:dm?"#D7CCC8":C.text, fontWeight:500 }}>Final Recommendation</span>
            <span style={{ fontWeight:800, color:recColor(a.rec), fontSize:16 }}>{a.rec}</span>
          </div>
        </AgentCard>
      </div>

      {/* What-If */}
      <Card dm={dm} className="no-print" style={{ padding:"20px 24px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:600, color:dm?"#F5EFE8":C.primary, fontSize:15 }}>What-If Scenario</div>
          <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>Simulate removing Written Contract from evidence</div>
        </div>
        <button onClick={onWhatIf} disabled={!analysis.evidence?.includes("Written contract")}
          style={{ padding:"9px 18px", background:whatIf?C.accent:C.primary, color:whatIf?"#4E342E":"#fff",
                   border:"none", borderRadius:8, fontSize:13, fontWeight:600,
                   opacity:!analysis.evidence?.includes("Written contract")?0.5:1 }}>
          {whatIf ? "✓ Scenario Active" : "Run What-If →"}
        </button>
      </Card>
    </div>
  );
}

// ─── HISTORY PAGE ─────────────────────────────────────────────────────────────
function HistoryPage({ cases, dm }) {
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? cases : cases.filter(c => c.risk === filter);
  const recColor = r => r==="Proceed"?C.success:r==="Revise"?C.warning:C.danger;

  return (
    <div className="fade-in">
      <div style={{ marginBottom:24 }}>
        <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:28, fontWeight:700, color:dm?"#F5EFE8":C.primary }}>Case History</h1>
        <p style={{ color:C.muted, fontSize:14, marginTop:4 }}>Complete CasusAI record of analyzed cases</p>
      </div>

      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
        {["All","Low","Moderate","High"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ padding:"7px 16px", borderRadius:20, border:`1px solid ${filter===f?C.primary:C.border}`,
                     background:filter===f?C.primary:"none", color:filter===f?"#fff":C.muted, fontSize:13, fontWeight:filter===f?600:400 }}>
            {f === "All" ? "All Cases" : `${f} Risk`}
          </button>
        ))}
        <div style={{ marginLeft:"auto", color:C.muted, fontSize:13, display:"flex", alignItems:"center" }}>
          {filtered.length} case{filtered.length!==1?"s":""}
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {filtered.map((c,i) => (
          <Card key={c.id} dm={dm} style={{ padding:"18px 22px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12, animation:`fadeIn .3s ease ${i*.05}s both` }}>
            <div style={{ flex:1, minWidth:200 }}>
              <div style={{ fontWeight:600, fontSize:14, color:dm?"#F5EFE8":C.text, marginBottom:3 }}>{c.title}</div>
              <div style={{ fontSize:12, color:C.muted }}>{c.date} · ₹{c.financial.toLocaleString("en-IN")}</div>
            </div>
            <div style={{ display:"flex", gap:16, alignItems:"center", flexWrap:"wrap" }}>
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:10, color:C.muted, textTransform:"uppercase", letterSpacing:".06em" }}>Score</div>
                <div style={{ fontSize:18, fontWeight:800, color:c.score>=70?C.success:c.score>=45?C.warning:C.danger, fontFamily:"'Playfair Display',serif" }}>{c.score}</div>
              </div>
              <span style={{ padding:"4px 12px", borderRadius:20, fontSize:12, fontWeight:600,
                background:c.risk==="Low"?"rgba(46,125,50,.1)":c.risk==="Moderate"?"rgba(230,81,0,.1)":"rgba(198,40,40,.1)",
                color:c.risk==="Low"?C.success:c.risk==="Moderate"?C.warning:C.danger }}>
                {c.risk} Risk
              </span>
              <span style={{ fontWeight:700, color:recColor(c.rec), fontSize:13 }}>{c.rec}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ─── ROOT APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage]           = useState("login");
  const [user, setUser]           = useState(null);
  const [dm,   setDm]             = useState(false);
  const [sidebarOpen, setSidebar] = useState(true);
  const [cases, setCases]         = useState(MOCK_CASES);
  const [analysis, setAnalysis]   = useState(null);
  const [form, setForm]           = useState({ desc:"", evidence:[], financial:"" });
  const [analyzing, setAnalyzing] = useState(false);
  const [whatIf, setWhatIf]       = useState(false);
  const [whatIfData, setWhatIfData] = useState(null);

  // Inject global CSS once
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  // Dark-mode class on body
  useEffect(() => {
    document.body.style.background = dm ? "#1a1008" : C.cream;
    document.body.style.color = dm ? "#F5EFE8" : C.text;
  }, [dm]);

  const handleLogin = (email, password) => {
    const u = USERS_DB[email];
    if (u && u.password === password) { setUser({ ...u, email }); setPage("dashboard"); }
    else alert("Invalid credentials.\nTry: admin@casusai.com / Demo@1234");
  };

  const handleRegister = (name, email, password) => {
    USERS_DB[email] = { password, name, role:"Associate" };
    setUser({ name, email, role:"Associate" });
    setPage("dashboard");
  };

  const handleAnalyze = () => {
    if (!form.desc || !form.evidence.length || !form.financial) { alert("Please fill all fields."); return; }
    setAnalyzing(true);
    setTimeout(() => {
      const result = generateAnalysis(form.desc, form.evidence, parseInt(form.financial));
      setAnalysis({ ...result, desc:form.desc, evidence:form.evidence });
      setCases(prev => [{
        id: prev.length + 1,
        title: form.desc.slice(0,40) + (form.desc.length>40?"…":""),
        date: new Date().toISOString().split("T")[0],
        score: result.score, risk: result.risk, rec: result.rec, financial: parseInt(form.financial),
      }, ...prev]);
      setAnalyzing(false); setWhatIf(false); setWhatIfData(null);
      setPage("results");
    }, 2800);
  };

  const handleWhatIf = () => {
    const ev = form.evidence.filter(e => e !== "Written contract");
    setWhatIfData(generateAnalysis(form.desc, ev, parseInt(form.financial), false));
    setWhatIf(true);
  };

  const stats = {
    total    : cases.length,
    avgScore : Math.round(cases.reduce((a,c) => a+c.score, 0) / cases.length),
    highRisk : cases.filter(c => c.risk==="High").length,
    strong   : cases.filter(c => c.score>=70).length,
  };

  // ── Auth screens ──
  if (!user) {
    return page === "login"
      ? <LoginPage    onLogin={handleLogin}   onGoRegister={() => setPage("register")} dm={dm}/>
      : <RegisterPage onRegister={handleRegister} onGoLogin={() => setPage("login")}  dm={dm}/>;
  }

  // ── Main app ──
  return (
    <div className={dm?"dm-root":""} style={{ display:"flex", minHeight:"100vh", background:dm?"#1a1008":C.cream }}>
      <Sidebar open={sidebarOpen} page={page} setPage={setPage} user={user} onLogout={() => { setUser(null); setPage("login"); }} dm={dm}/>

      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
        {/* Topbar */}
        <nav className={`dm-nav no-print`}
          style={{ background:dm?"#1a1008":"#fff", borderBottom:`1px solid ${dm?"#3d2a1f":C.border}`,
                   padding:"0 24px", height:64, display:"flex", alignItems:"center", justifyContent:"space-between",
                   position:"sticky", top:0, zIndex:100 }}>
          <div style={{ display:"flex", alignItems:"center", gap:16 }}>
            <button onClick={() => setSidebar(o => !o)} style={{ background:"none", border:"none", padding:8, color:C.primary, fontSize:20 }}>☰</button>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, color:C.primary, fontSize:20 }}>Casus</span>
              <span style={{ background:C.accent, color:"#4E342E", fontWeight:700, fontSize:12, padding:"2px 7px", borderRadius:4, letterSpacing:".04em" }}>AI</span>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <button onClick={() => setDm(d => !d)}
              style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:20, padding:"6px 14px", fontSize:13, color:dm?"#F5EFE8":C.text }}>
              {dm ? "☀ Light" : "◑ Dark"}
            </button>
            <div style={{ width:36, height:36, borderRadius:"50%", background:C.primary, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:14, fontWeight:600 }}>
              {user.name[0]}
            </div>
          </div>
        </nav>

        {/* Page content */}
        <main style={{ flex:1, padding:"28px 28px 48px", overflowY:"auto" }}>
          {page === "dashboard" && <Dashboard stats={stats} cases={cases} setPage={setPage} dm={dm}/>}
          {page === "analyze"   && <AnalyzePage form={form} setForm={setForm} onSubmit={handleAnalyze} analyzing={analyzing} dm={dm}/>}
          {page === "results"   && analysis && (
            <ResultsPage
              analysis={analysis} whatIf={whatIf} whatIfAnalysis={whatIfData}
              onWhatIf={handleWhatIf} onDownload={() => window.print()}
              onNewCase={() => { setPage("analyze"); setForm({ desc:"", evidence:[], financial:"" }); }}
              dm={dm}
            />
          )}
          {page === "history"   && <HistoryPage cases={cases} dm={dm}/>}
        </main>
      </div>
    </div>
  );
}
